import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { SlidersHorizontal, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { ArtistCard } from "@/components/ArtistCard";
import { SEO } from "@/components/SEO";

const SERVICE_FILTERS = ["Bridal Makeup", "Engagement Makeup", "Reception Makeup", "Party & Occasion Makeup", "HD Makeup", "Airbrush Makeup", "Hairstyling", "Draping"];
const PRICE_FILTERS = [
  { value: "all", label: "Any Price" },
  { value: "0-5000", label: "Under ₹5,000" },
  { value: "5000-10000", label: "₹5,000 – ₹10,000" },
  { value: "10000-99999999", label: "₹10,000+" },
];

export default function Artists({ initialCity = "" }) {
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [city, setCity] = useState(initialCity);
  const [service, setService] = useState("");
  const [price, setPrice] = useState("all");
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    api.get("/artists").then((r) => setArtists(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const cities = useMemo(() => {
    const set = new Set();
    artists.forEach((a) => {
      if (a.location) set.add(a.location);
      (a.cities_served || []).forEach((c) => set.add(c));
    });
    return Array.from(set);
  }, [artists]);

  const filtered = useMemo(() => {
    return artists.filter((a) => {
      if (city) {
        const cl = city.toLowerCase();
        const inCity = (a.location || "").toLowerCase() === cl || (a.cities_served || []).some((c) => c.toLowerCase() === cl);
        if (!inCity) return false;
      }
      if (service && !(a.services || []).some((s) => s.toLowerCase() === service.toLowerCase())) return false;
      if (price !== "all") {
        const [min, max] = price.split("-").map(Number);
        const p = a.starting_price || 0;
        if (p < min || p > max) return false;
      }
      return true;
    });
  }, [artists, city, service, price]);

  const hasFilters = city || service || price !== "all";

  return (
    <div data-testid="artists-page" className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-10 sm:py-16">
      <SEO
        title={initialCity ? `Makeup Artists in ${initialCity}` : "Makeup Artists"}
        description="Browse professional makeup artists — bridal, engagement, party, HD & airbrush makeup with transparent rates. Lucknow | Available for travel."
        path={initialCity ? `/makeup-artists/${initialCity.toLowerCase()}` : "/makeup-artists"}
      />
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] font-semibold text-velvet">Find Your Artist</p>
          <h1 className="mt-2 font-serif font-bold text-ink text-3xl sm:text-4xl lg:text-5xl">
            Makeup Artists{initialCity ? ` in ${initialCity}` : ""}
          </h1>
        </div>
        <Button
          data-testid="toggle-filters-btn"
          variant="outline"
          className="rounded-full border-ink/25 lg:hidden"
          onClick={() => setShowFilters((v) => !v)}
        >
          <SlidersHorizontal className="w-4 h-4 mr-2" /> Filters
        </Button>
      </div>

      <div className={`mt-6 lg:block ${showFilters ? "block" : "hidden"}`} data-testid="filters-panel">
        <div className="bg-white rounded-2xl border border-[#EAE5DF] p-4 sm:p-5 flex flex-col gap-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Select value={city || "all"} onValueChange={(v) => setCity(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="filter-city" className="rounded-full"><SelectValue placeholder="City" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="filter-city-all">All Cities</SelectItem>
                {cities.map((c) => (
                  <SelectItem key={c} value={c} data-testid={`filter-city-${c.toLowerCase().replace(/\s+/g, "-")}`}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={price} onValueChange={setPrice}>
              <SelectTrigger data-testid="filter-price" className="rounded-full"><SelectValue placeholder="Price range" /></SelectTrigger>
              <SelectContent>
                {PRICE_FILTERS.map((p) => (
                  <SelectItem key={p.value} value={p.value} data-testid={`filter-price-${p.value}`}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-wrap gap-2">
            {SERVICE_FILTERS.map((s) => (
              <button
                key={s}
                data-testid={`filter-service-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => setService(service === s ? "" : s)}
                className={`text-xs font-semibold px-4 py-2 rounded-full border transition-colors duration-200 ${
                  service === s ? "bg-velvet border-velvet text-ink" : "border-ink/15 text-ink/60 hover:border-velvet hover:text-ink"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
          {hasFilters && (
            <button data-testid="clear-filters-btn" onClick={() => { setCity(""); setService(""); setPrice("all"); }}
              className="self-start flex items-center gap-1 text-xs font-semibold text-velvet hover:text-velvethover">
              <X className="w-3.5 h-3.5" /> Clear all filters
            </button>
          )}
        </div>
      </div>

      <p className="mt-6 text-sm text-ink/50" data-testid="artists-count">
        {loading ? "Loading artists…" : `${filtered.length} artist${filtered.length === 1 ? "" : "s"} found`}
      </p>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
        {filtered.map((a, i) => (
          <motion.div key={a.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.08 }}>
            <ArtistCard artist={a} />
          </motion.div>
        ))}
      </div>
      {!loading && filtered.length === 0 && (
        <div data-testid="no-artists-message" className="text-center py-20 text-ink/50">
          <p className="font-serif text-2xl text-ink">No artists match your filters</p>
          <p className="mt-2 text-sm">Try clearing a filter, or call us — we may still be able to help.</p>
        </div>
      )}
    </div>
  );
}
